import { Typography, Button } from '@frontend/design_system'
import { useState } from 'react'
import { ChevronIcon } from 'ui/icons/ChevronIcon'
import { ArrowChangeIcon, DownloadIcon } from 'ui/icons'
import { DocumentItem } from 'ui/atoms/DocumentItem'
import { isOperatorSelector } from 'redux/login/selectors'
import { useAppSelector } from 'redux/hooks'
import { format } from 'date-fns'
import { DOCUMENTS_UPDATE_FORMAT } from 'constants/date'
import { StatusBadge } from 'ui/atoms/StatusBadge'
import { StatusBadgeProps } from 'ui/atoms/StatusBadge/types'
import { STATUS_BADGE_STATUSES } from 'texts/pendingForApproval'
import { documentsApi } from 'api/documents'
import { useApiRequest } from 'hooks/useApiRequest'
import { useCompanyId } from 'hooks/useCompanyId'
import { statusBadge } from './constants'
import { FileListProps } from './types'
import styles from './styles.module.scss'
import { REGISTRATION_STATUSES } from 'api/auth/constants'
import { DOWNLOAD_ALL, LAST_UPDATED, REPLACE_FILES } from 'texts/documents'

const ROTATE_ANGLE = 180

export const FileList = ({
  isArchived,
  documents,
  date,
  status,
  handleReplaceModalOpen,
  documentType,
}: FileListProps) => {
  const [isListOpened, setIsListOpened] = useState(!isArchived)

  const isOperator = useAppSelector(isOperatorSelector)
  const companyId = useCompanyId()

  const { apiRequest: downloadAllDocuments, loading: isDownloadingDocuments } =
    useApiRequest(
      (date) =>
        documentsApi.downloadAllDocuments(documentType, date, companyId),
      undefined,
      true,
      undefined,
      false
    )

  const handleDownloadAllDocuments = async () => {
    if (date) {
      const response = await downloadAllDocuments(date)
      if (response?.data) {
        const fileName = response.headers
          .get('content-disposition')
          ?.split('filename=')[1]
          .replaceAll('"', '')
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName as string)
        document.body.appendChild(link)
        link.click()
      }
    }
  }

  return (
    <>
      <div className={styles['file-list-header']}>
        <Typography
          name="body2WMedium"
          className={styles['file-list-header-title']}
          onClick={() => setIsListOpened(!isListOpened)}
        >
          <div className={styles['chevron-icon']}>
            <ChevronIcon
              size="small"
              color="blackMaster"
              rotate={isListOpened ? ROTATE_ANGLE : 0}
            />
          </div>
          Uploaded files ({documents.length})
        </Typography>
        <div className={styles['file-list-header-info']}>
          {isOperator && (
            <Typography name="body2WRegular" color="grey800">
              {LAST_UPDATED}{' '}
              {date ? format(new Date(date), DOCUMENTS_UPDATE_FORMAT) : ''}
            </Typography>
          )}
          {!isArchived && isOperator && (
            <StatusBadge
              label={STATUS_BADGE_STATUSES[status]}
              status={statusBadge[status] as StatusBadgeProps['status']}
            />
          )}
          {!isArchived &&
            [
              REGISTRATION_STATUSES.REQUESTED,
              REGISTRATION_STATUSES.APPROVED,
              REGISTRATION_STATUSES.REJECTED,
            ].includes(status) && (
              <Button
                label={REPLACE_FILES}
                variant="secondary-border"
                typographyName="Button2"
                size="small"
                onClick={handleReplaceModalOpen}
                iconRight={<ArrowChangeIcon color="blue700" />}
                widthClassName={styles['button-wrapper']}
              />
            )}
          <Button
            label={DOWNLOAD_ALL}
            variant="secondary-border"
            typographyName="Button2"
            size="small"
            onClick={handleDownloadAllDocuments}
            iconRight={<DownloadIcon color="blue700" />}
            widthClassName={styles['button-wrapper']}
            loading={isDownloadingDocuments}
          />
        </div>
      </div>
      {isListOpened && (
        <div className={styles['file-list']}>
          {documents.map((document) => (
            <DocumentItem
              key={document.documentKey}
              type={document.documentDisplayName.split('.')[1]}
              name={document.documentDisplayName}
              url={document.fileLink}
              withDownload
            />
          ))}
        </div>
      )}
    </>
  )
}
